<template lang="pug">
#send-other-user-safety-confirm(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 施設関係者へ安否確認入力依頼のメッセージ送信

    .box-body.register-body
      TargetSelectButton(v-model="groups" :isBusy="isBusy" @onTargetBtn="onTargetBtn")

      FormTextarea(
        key-name="メッセージ内容"
        name="text"
        v-model="openingSentence"
        placeholder="施設関係者に送るメッセージ内容を入力してください\n\n例：\n安否確認のテストを実施します。\nみなさんの仮の安否情報を入力してください。"
        :validation-rules="{ max: 300, required: true }"
        :maxlength="300"
        :styles="{ maxWidth: '760px', width: '760px', minHeight: '160px' }" required)

      FormLabel(key-name="質問項目" :value="confirms")

      span.error-message {{ errorMessage }}

    .box-footer.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="previewConfirm" type="button") プレビュー
        button.btn.register-btn(@click="showConfirmSendMessageModal" type="button") 送信
        button.btn.cancel-btn(@click="gotoSafetyConfirmList" type="button") キャンセル

  PreviewOtherUserTemplateModal(v-model="showPreviewModal" :confirms="previewConfirms")

  GroupSelectModal(ref="groupSelectModal" :groupType.sync="groupType" @add="addSendGroupTarget")

  OkCancelModal(v-model="showSendMessageModal" title="安否確認を送信しますか？" @ok="sendSafetyConfirm")
  OkModal(v-model="showSendMessageSuccessModal" title="送信を受け付けました。" @ok="gotoSafetyConfirmList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";
import OtherUserSafetyConfirmsTemplatesService, { OtherUserSafetyConfirmsTemplatesGetModel } from "@/commons/services/OtherUserSafetyConfirmsTemplates";
import SendMessageGroupsService, { GroupType, SendMessageGroupsGetModel } from "@/commons/services/SendMessageGroups";
import OtherUserSafetyConfirmMastersService, { OtherUserSafetyConfirmMastersGetModel } from "@/commons/services/OtherUserSafetyConfirmMasters";
import SendSafetyConfirmMessagesService, {
  OtherUserSendSafetyConfirmLineMessagesPostModel,
  OtherUserSendSafetyConfirmMailMessagesPostModel,
  SafetyConfirmTarget
} from "@/commons/services/SendSafetyConfirmMessages";
import SendMessageGroupMembersService, { SendMessageGroupMembersOtherUserGroupMemberGetModel } from "@/commons/services/SendMessageGroupMembers";
import OtherUserGroup, { SenderType } from "@/commons/models/OtherUserGroup";
import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import TargetSelectButton, { GroupModel, OtherUserGroupTargetModel, SendMessageGroupTargetModel } from "@/pc/containers/safetyConfirms/TargetSelectButton.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import { UseId } from "./OtherUserTemplateEdit.vue";
import PreviewOtherUserTemplateModal from "@/pc/containers/safetyConfirms/PreviewOtherUserTemplateModal.vue";
import GroupSelectModal from "@/pc/containers/safetyConfirms/GroupSelectModal.vue";

/**
 * ### 安否確認登録画面コンポーネント
 */
@Component({
  components: {
    TargetSelectButton,
    FormTextarea,
    FormLabel,
    PageLoading,
    ValidationAlert,
    OkCancelModal,
    OkModal,
    PreviewOtherUserTemplateModal,
    GroupSelectModal
  },
  $_veeValidate: { validator: "new" }
})
export default class SendOtherUserSafetyConfirm extends Vue {
  /** 安否確認(施設関係者)テンプレートサービス */
  @Inject("OtherUserSafetyConfirmsTemplatesService") otherUserSafetyConfirmsTemplatesService!: OtherUserSafetyConfirmsTemplatesService;
  /** 安否確認(施設関係者)で送信する質問内容のマスタサービス */
  @Inject("OtherUserSafetyConfirmMastersService") otherUserSafetyConfirmMastersService!: OtherUserSafetyConfirmMastersService;
  /** 宛先グループサービス */
  @Inject("SendMessageGroupsService") sendMessageGroupsService!: SendMessageGroupsService;
  /** 安否確認(施設関係者)メッセージ送信サービス */
  @Inject("SendSafetyConfirmMessagesService") sendSafetyConfirmMessagesService!: SendSafetyConfirmMessagesService;
  /** 宛先グループメンバーサービス */
  @Inject("SendMessageGroupMembersService") sendMessageGroupMembersService!: SendMessageGroupMembersService;
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 施設関係者グループメンバー取得サービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showSendMessageModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showSendMessageSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 安否確認(施設関係者)で送信する質問内容のマスタ */
  masters: Array<OtherUserSafetyConfirmMastersGetModel> = [];
  /** 安否確認(施設関係者)テンプレート */
  template: OtherUserSafetyConfirmsTemplatesGetModel | null = null;

  /** グループ */
  groups: GroupModel = {
    familyGroups: [],
    otherUserGroups: [],
    residentGroups: [],
    otherUserGroupMemberGroups: []
  };
  /** グループ種別 */
  groupType: GroupType = GroupType.UNKNOWN;
  /** 施設関係者グループメンバーのMAP。key：施設関係者グループID、value：施設関係者グループの配列 */
  otherUserGroupMemberMap: Map<number, Array<OtherUserGroup>> = new Map();

  /** 冒頭文 */
  openingSentence: string = "";
  /** 質問リスト */
  confirms: string = "";

  /** プレビューモーダル表示可否 */
  showPreviewModal: boolean = false;
  /** プレビュー用質問リスト */
  previewConfirms: Array<UseId> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   *
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 宛先選択肢のグループ情報を作成する
    const sendMessageGroups: Array<SendMessageGroupsGetModel> = await this.sendMessageGroupsService.gets(userStore);
    const otherUserGroups: Array<OtherUserGroup> = await this.otherUserGroupsService.gets(userStore);
    const otherUserGroupMembersInFacility: Array<OtherUserGroup> = await this.otherUserGroupMembersService.get(userStore);
    const otherUserGroupMemberMap: Map<number, Array<OtherUserGroup>> = this.createOtherUserGroupMemberMap(otherUserGroupMembersInFacility);
    this.groups = await this.createTargetGroups(sendMessageGroups, otherUserGroups, otherUserGroupMemberMap);
    this.otherUserGroupMemberMap = otherUserGroupMemberMap;

    // テンプレートから入力情報を転記する
    const template: OtherUserSafetyConfirmsTemplatesGetModel = await this.getTemplate();
    this.template = template;
    // 宛先情報の復元は分岐が必要
    // v3.33.0以前のバージョンは宛先グループのgroupId（単体）を保持している
    // v3.34.0以降のバージョンは宛先情報（複数）を保持している
    // 宛先情報（複数）が存在する場合はv3.34.0以降のデータと判断し、存在せずgroupIdが存在する場合はv3.33.0以前のデータと判断する
    if (template.targets) {
      this.restoreTargetsByLatest(template.targets, this.groups);
    } else if (template.groupId) {
      this.restoreTargetsByOld(template, this.groups);
    }
    this.openingSentence = template.openingSentence;

    // 安否情報を作成する
    const masters: Array<OtherUserSafetyConfirmMastersGetModel> = await this.getOtherUserSafetyConfirmMasters();
    this.masters = masters;
    const mastersMap: Map<number, OtherUserSafetyConfirmMastersGetModel> = this.createMasterMap(masters);
    const confirms: string = this.createConfirms(template, mastersMap);
    this.confirms = confirms;
    const previewConfirms: Array<UseId> = this.createPreviewConfirms(template, masters);
    this.previewConfirms = previewConfirms;

    this.isBusy = false;

    return;
  }

  private createOtherUserGroupMemberMap(otherUserGroupMembersInFacility: Array<OtherUserGroup>): Map<number, Array<OtherUserGroup>> {
    const map: Map<number, Array<OtherUserGroup>> = new Map<number, Array<OtherUserGroup>>();
    for (const member of otherUserGroupMembersInFacility) {
      const members: Array<OtherUserGroup> | undefined = map.get(member.id);
      if (members) {
        members.push(member);
        map.set(member.id, members);
      } else {
        map.set(member.id, [member]);
      }
    }
    return map;
  }

  /**
   * ### 宛先のグループ情報を作成する
   */
  private async createTargetGroups(
    sendMessageGroups: Array<SendMessageGroupsGetModel>,
    otherUserGroups: Array<OtherUserGroup>,
    otherUserGroupMemberMap: Map<number, Array<OtherUserGroup>>
  ): Promise<GroupModel> {
    // 宛先グループの情報作成
    const targetOtherUserGroupMemberGroups: Array<SendMessageGroupTargetModel> = [];
    for (const group of sendMessageGroups) {
      const _group: SendMessageGroupTargetModel = Object.assign({}, group) as SendMessageGroupTargetModel;
      _group.isTarget = false;
      _group.memberCount = group.members.length;
      if (_group.type == GroupType.OTHER_USER) {
        targetOtherUserGroupMemberGroups.push({
          ..._group,
          show: true
        });
      }
    }
    // 施設関係者グループの情報を作成
    const targetOtherUserGroups: Array<OtherUserGroupTargetModel> = [];
    for (const group of otherUserGroups) {
      const _group: OtherUserGroupTargetModel = Object.assign({}, group) as OtherUserGroupTargetModel;
      _group.isTarget = false;
      const members: Array<OtherUserGroup> | undefined = otherUserGroupMemberMap.get(_group.id);
      _group.memberCount = members ? members.length : 0;
      targetOtherUserGroups.push({
        ..._group,
        key: `otherUserGroup_${_group.id}`,
        show: true
      });
    }

    return {
      familyGroups: [],
      otherUserGroupMemberGroups: targetOtherUserGroupMemberGroups,
      residentGroups: [],
      otherUserGroups: targetOtherUserGroups
    };
  }

  /**
   * ### 安否確認(施設関係者)テンプレートを取得する
   *
   * - 現時点ではテンプレートは1件のみなのでid指定は1固定
   */
  public async getTemplate(): Promise<OtherUserSafetyConfirmsTemplatesGetModel> {
    const response: OtherUserSafetyConfirmsTemplatesGetModel = await this.otherUserSafetyConfirmsTemplatesService.get(userStore, 1);
    return response;
  }

  /**
   * ### 宛先を復元する（v3.33.0以前）
   *
   * - 宛先グループIDで検索して一致するものを宛先対象とする
   * - v3.33.0以前は宛先グループ（施設関係者）しか宛先に設定できなかったの検索対象はotherUserGroupMemberGroupsのみ
   */
  private restoreTargetsByOld(template: OtherUserSafetyConfirmsTemplatesGetModel, groups: GroupModel): void {
    for (const group of groups.otherUserGroupMemberGroups) {
      if (group.id == template.groupId) {
        group.isTarget = true;
        return;
      }
    }
    return;
  }

  /**
   * ### 宛先を復元する（v3.34.0以降）
   *
   * - ソートキーリストで検索して一致するものを宛先対象とする
   * - 宛先グループ（施設関係者）、施設関係者グループのみが対象となる
   */
  private restoreTargetsByLatest(targets: string[], groups: GroupModel): void {
    // 検索用にmapにする
    const map: Map<string, string> = new Map<string, string>();
    for (const target of targets) {
      map.set(target, target);
    }

    for (const group of groups.otherUserGroupMemberGroups) {
      if (map.has(String(group.id))) {
        group.isTarget = true;
      }
    }
    for (const group of groups.otherUserGroups) {
      if (map.has(group.otherUserGroupsSortKey)) {
        group.isTarget = true;
      }
    }
    return;
  }

  /**
   * ### 安否確認(施設関係者)で送信する質問内容のマスタを取得する
   */
  public async getOtherUserSafetyConfirmMasters(): Promise<Array<OtherUserSafetyConfirmMastersGetModel>> {
    const response: Array<OtherUserSafetyConfirmMastersGetModel> = await this.otherUserSafetyConfirmMastersService.get(userStore);
    return response;
  }

  /**
   * ### 質問内容マスタのMAPを作成する
   */
  public createMasterMap(masters: Array<OtherUserSafetyConfirmMastersGetModel>): Map<number, OtherUserSafetyConfirmMastersGetModel> {
    const map: Map<number, OtherUserSafetyConfirmMastersGetModel> = new Map<number, OtherUserSafetyConfirmMastersGetModel>();
    for (const master of masters) {
      map.set(master.id, master);
    }
    return map;
  }

  /**
   * ### 使用する質問リストを作成する
   *
   * - テンプレート情報から質問を使用するかしないかを設定する
   */
  public createConfirms(template: OtherUserSafetyConfirmsTemplatesGetModel, map: Map<number, OtherUserSafetyConfirmMastersGetModel>): string {
    const confirms: string[] = [];
    for (const id of template.useIds) {
      const confirm: OtherUserSafetyConfirmMastersGetModel | undefined = map.get(id);
      if (confirm != undefined) {
        confirms.push(confirm.label);
      }
    }
    return confirms.join("、");
  }

  public createPreviewConfirms(template: OtherUserSafetyConfirmsTemplatesGetModel, masters: Array<OtherUserSafetyConfirmMastersGetModel>): Array<UseId> {
    const confirms: Array<UseId> = [];
    for (const master of masters) {
      for (const useId of template.useIds) {
        if (master.id == useId) {
          confirms.push({
            ...master,
            use: true,
            choicesLabel: ""
          });
        }
      }
    }
    return confirms;
  }

  /**
   * ### 宛先モーダルを表示する
   */
  onTargetBtn(): void {
    // メモリ渡しだと値変更が共有されてしまう
    // 値変更後に「キャンセル」された場合に困るので、コピーしたものを渡す
    const copy: GroupModel = JSON.parse(JSON.stringify(this.groups));
    const modal: any = this.$refs.groupSelectModal;
    modal.show(copy);
    return;
  }

  /**
   * ### 宛先モーダルの情報を更新する
   */
  addSendGroupTarget(targets: GroupModel): void {
    this.groups = targets;
    return;
  }

  /**
   * ### 登録確認ダイアログを表示する
   *
   * - バリデーションを実施する
   * - エラーでなければ確認ダイアログを表示する
   */
  public async showConfirmSendMessageModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    const validationTargets: boolean = this.validateTargets();
    if (validationResult && validationTargets) {
      this.showValidationAlert = false;
      this.showSendMessageModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  private validateTargets(): boolean {
    this.$validator.errors.remove("target-groups");
    for (const group of this.groups.otherUserGroupMemberGroups) {
      if (group.isTarget) {
        return true;
      }
    }
    for (const group of this.groups.otherUserGroups) {
      if (group.isTarget) {
        return true;
      }
    }
    this.$validator.errors.add({ field: "target-groups", msg: "宛先は必須です" });
    return false;
  }

  /**
   * ### 安否確認を送信する
   *
   * - ローディングを表示
   * - 安否確認サービスを使用して安否確認を登録する
   * - 例外が無ければ成功ダイアログを表示する
   */
  public async sendSafetyConfirm(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "送信中...";

    try {
      const safetyConfirmsId: number = Number(this.$route.params.id);

      const requestBodyForLine: OtherUserSendSafetyConfirmLineMessagesPostModel = await this.createRequestBodyForLine(this.groups, this.otherUserGroupMemberMap);
      const requestBodyForMail: OtherUserSendSafetyConfirmMailMessagesPostModel = await this.createRequestBodyForMail(this.groups, this.otherUserGroupMemberMap);

      if (requestBodyForLine.targets.length < 1 && requestBodyForMail.targets.length < 1) {
        this.$validator.errors.add({ field: "target-groups", msg: "送信対象の施設関係者がいないため、メッセージを送ることができません" });
        this.showValidationAlert = true;
      } else {
        this.showValidationAlert = false;
        if (0 < requestBodyForLine.targets.length) {
          await this.sendSafetyConfirmMessagesService.sendOtherUserMessage(userStore, safetyConfirmsId, requestBodyForLine);
        }
        if (0 < requestBodyForMail.targets.length) {
          await this.sendSafetyConfirmMessagesService.sendOtherUserMails(userStore, safetyConfirmsId, requestBodyForMail);
        }

        this.showSendMessageSuccessModal = true;
      }
    } catch (error) {
      const systemError: SystemError = error as SystemError;

      // ステータスコードが400の場合、エラーメッセージを表示し画面情報を再取得する
      if (systemError.statusCode == 400 && 0 < systemError.originalResponse.indexOf("other user safety confirmation has already been sent.")) {
        this.createView();
        this.errorMessage = "既に施設関係者（職員）の安否確認を送信しているため送信できません。";
      } else {
        throw error;
      }
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### 安否確認登録リクエストボディ(LINE)作成
   */
  public async createRequestBodyForLine(groups: GroupModel, otherUserGroupMemberMap: Map<number, Array<OtherUserGroup>>): Promise<OtherUserSendSafetyConfirmLineMessagesPostModel> {
    const targets: Array<SafetyConfirmTarget> = [];

    // 宛先グループ（施設関係者）のメンバーのLINEユーザーで宛先を作成する
    for (const group of groups.otherUserGroupMemberGroups) {
      if (group.isTarget) {
        const sortKeys: Array<string> = [];
        const members: Array<SendMessageGroupMembersOtherUserGroupMemberGetModel> = await this.getSendMessageGroupOtherGroupUserMembers(group.id as number);

        for (const member of members) {
          if (member.senderType == SenderType.LINE && member.memberLineId) {
            sortKeys.push(member.otherUserGroupsSortKey);
          }
        }

        if (0 < sortKeys.length) {
          targets.push({
            groupName: group.name,
            sortKeys
          });
        }
      }
    }
    // 施設関係者グループのメンバーのLINEユーザーで宛先を作成する
    for (const group of groups.otherUserGroups) {
      if (group.isTarget) {
        if (otherUserGroupMemberMap.has(group.id)) {
          const sortKeys: Array<string> = [];

          for (const member of otherUserGroupMemberMap.get(group.id) as Array<OtherUserGroup>) {
            if (member.senderType == SenderType.LINE && member.memberLineId) {
              sortKeys.push(member.otherUserGroupsSortKey);
            }
          }

          if (0 < sortKeys.length) {
            targets.push({
              groupName: group.name,
              sortKeys
            });
          }
        }
      }
    }

    return {
      targets,
      message: this.openingSentence,
      useIds: this.template ? this.template.useIds : []
    };
  }

  /**
   * ### 安否確認登録リクエストボディ(メール)作成
   */
  public async createRequestBodyForMail(groups: GroupModel, otherUserGroupMemberMap: Map<number, Array<OtherUserGroup>>): Promise<OtherUserSendSafetyConfirmMailMessagesPostModel> {
    const targets: Array<SafetyConfirmTarget> = [];

    // 宛先グループ（施設関係者）のメンバーのLINEユーザーで宛先を作成する
    for (const group of groups.otherUserGroupMemberGroups) {
      if (group.isTarget) {
        const sortKeys: Array<string> = [];
        const members: Array<SendMessageGroupMembersOtherUserGroupMemberGetModel> = await this.getSendMessageGroupOtherGroupUserMembers(group.id as number);

        for (const member of members) {
          if (member.senderType == SenderType.MAIL && member.memberMailaddress) {
            sortKeys.push(member.otherUserGroupsSortKey);
          }
        }

        if (0 < sortKeys.length) {
          targets.push({
            groupName: group.name,
            sortKeys
          });
        }
      }
    }
    // 施設関係者グループのメンバーのLINEユーザーで宛先を作成する
    for (const group of groups.otherUserGroups) {
      if (group.isTarget) {
        if (otherUserGroupMemberMap.has(group.id)) {
          const sortKeys: Array<string> = [];

          for (const member of otherUserGroupMemberMap.get(group.id) as Array<OtherUserGroup>) {
            if (member.senderType == SenderType.MAIL && member.memberMailaddress) {
              sortKeys.push(member.otherUserGroupsSortKey);
            }
          }

          if (0 < sortKeys.length) {
            targets.push({
              groupName: group.name,
              sortKeys
            });
          }
        }
      }
    }

    return {
      targets: targets,
      message: this.openingSentence
    };
  }

  /**
   * ### 宛先グループを取得する
   */
  public async getSendMessageGroup(groupId: number): Promise<SendMessageGroupsGetModel> {
    const response: SendMessageGroupsGetModel = await this.sendMessageGroupsService.get(userStore, groupId);
    return response;
  }

  /**
   * ### 宛先グループメンバーを取得する
   */
  public async getSendMessageGroupOtherGroupUserMembers(groupId: number): Promise<Array<SendMessageGroupMembersOtherUserGroupMemberGetModel>> {
    const members: Array<SendMessageGroupMembersOtherUserGroupMemberGetModel> = (await this.sendMessageGroupMembersService.get(
      userStore,
      groupId
    )) as Array<SendMessageGroupMembersOtherUserGroupMemberGetModel>;

    return members;
  }

  /** ### プレビューを表示する */
  public previewConfirm(): void {
    this.showPreviewModal = true;
    return;
  }

  /**
   * ### 安否確認一覧画面に遷移する
   */
  public gotoSafetyConfirmList(): void {
    this.$router.push(PcUrl.SafetyConfirmList);

    return;
  }
}
</script>

<style lang="scss" scoped>
#send-other-user-safety-confirm {
  max-width: 820px;
  margin-left: auto;
  margin-right: auto;

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
